import type { EasyModePlaceSelection } from "@/types/easyModePlace";
import type { GooglePlaceDetailsResult } from "@/utils/places/normalizeGooglePlace";

interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

const PLUS_CODE_PATTERN =
  /^[23456789CFGHJMPQRVWX]{4,}\+[23456789CFGHJMPQRVWX]{2,}$/i;

const STREET_NOISE_PATTERN =
  /\b(road|rd\.?|street|st\.?|avenue|ave\.?|lane|ln\.?|highway|hwy\.?|nh[-\s]?\d+|bypass|cross|nagar road)\b/i;

const COUNTRY_STATE_TOKENS = new Set(
  [
    "india",
    "telangana",
    "andhra pradesh",
    "karnataka",
    "maharashtra",
    "tamil nadu",
    "delhi",
    "new delhi",
    "uttar pradesh",
    "west bengal",
    "gujarat",
    "rajasthan",
    "punjab",
    "kerala",
    "haryana",
    "bihar",
    "odisha",
    "madhya pradesh",
    "assam",
    "jharkhand",
    "chhattisgarh",
    "goa",
    "uttarakhand",
    "himachal pradesh",
  ].map((s) => s.toLowerCase())
);

function getComponent(
  components: GoogleAddressComponent[],
  types: string[],
  nameType: "long_name" | "short_name" = "long_name"
): string | null {
  for (const type of types) {
    const match = components.find((c) => c.types.includes(type));
    const value = match?.[nameType]?.trim();
    if (value) return value;
  }
  return null;
}

function isPlusCode(value: string): boolean {
  return PLUS_CODE_PATTERN.test(value.trim().replace(/\s/g, ""));
}

function isCountryOrStateToken(value: string): boolean {
  return COUNTRY_STATE_TOKENS.has(value.trim().toLowerCase());
}

function isUsableAreaLabel(value: string | null | undefined): value is string {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (isPlusCode(trimmed)) return false;
  if (/^[\d#./\-\s,]+$/.test(trimmed)) return false;
  if (/^\d{5,6}$/.test(trimmed.replace(/\s/g, ""))) return false;
  if (isCountryOrStateToken(trimmed)) return false;
  if (STREET_NOISE_PATTERN.test(trimmed) && trimmed.split(/\s+/).length <= 2) {
    return false;
  }
  return true;
}

function normalizeKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function splitAddressParts(fullAddress: string): string[] {
  return fullAddress
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
}

/**
 * Parse city from formatted_address: segment before state, skip country/plus-code/street noise.
 */
function parseCityFromFormattedAddress(
  fullAddress: string,
  state: string | null
): string | null {
  const parts = splitAddressParts(fullAddress);
  if (parts.length === 0) return null;

  const stateKey = state ? normalizeKey(state) : "";
  let end = parts.length;
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const key = normalizeKey(parts[i]);
    if (isPlusCode(parts[i]) || isCountryOrStateToken(parts[i])) {
      end = i;
      continue;
    }
    if (stateKey && key === stateKey) {
      end = i;
      continue;
    }
    break;
  }

  const candidates = parts.slice(0, end).filter(isUsableAreaLabel);
  if (candidates.length === 0) return null;

  // Prefer last usable segment before state (usually city)
  return candidates[candidates.length - 1] ?? null;
}

/**
 * Prefer address segment immediately before city; filter noise.
 */
function extractAreaFromFormattedAddress(
  fullAddress: string,
  city: string
): string | null {
  const parts = splitAddressParts(fullAddress);
  const cityKey = normalizeKey(city);
  const cityIndex = parts.findIndex((p) => normalizeKey(p) === cityKey);

  if (cityIndex > 0) {
    for (let i = cityIndex - 1; i >= 0; i -= 1) {
      if (isUsableAreaLabel(parts[i])) return parts[i];
    }
  }

  for (const part of parts) {
    if (normalizeKey(part) === cityKey) continue;
    if (isUsableAreaLabel(part)) return part;
  }

  return null;
}

export type EasyModeResolveOptions = {
  /** Autocomplete structured_formatting.main_text */
  predictionMainText?: string | null;
};

/**
 * Step A + B: normalize Place Details and resolve product `city` / `area` (mobile Easy Mode).
 * Throws if geometry lat/lng is missing.
 */
export function resolveEasyModePlace(
  result: GooglePlaceDetailsResult,
  options: EasyModeResolveOptions = {}
): EasyModePlaceSelection {
  const lat = result.geometry?.location?.lat;
  const lng = result.geometry?.location?.lng;
  if (typeof lat !== "number" || typeof lng !== "number") {
    throw new Error("Place details missing geometry.location");
  }

  const components = (result.address_components ?? []) as GoogleAddressComponent[];
  const fullAddress = (result.formatted_address ?? "").trim();
  const detailsName = (result.name ?? "").trim();
  const types = result.types ?? [];
  const isEstablishment = types.includes("establishment") || types.includes("point_of_interest");

  const locality = getComponent(components, ["locality", "postal_town"]);
  const subLocality = getComponent(components, [
    "sublocality_level_1",
    "sublocality_level_2",
    "sublocality",
  ]);
  const neighborhood = getComponent(components, ["neighborhood"]);
  const state = getComponent(components, ["administrative_area_level_1"]);
  const country = getComponent(components, ["country"], "short_name");

  const displayName =
    getComponent(components, [
      "sublocality_level_1",
      "sublocality_level_2",
      "sublocality",
      "neighborhood",
      "locality",
      "postal_town",
      "administrative_area_level_3",
      "administrative_area_level_2",
    ]) ||
    detailsName ||
    fullAddress;

  const predictionMain = options.predictionMainText?.trim() || "";
  const streetLikeTypes = new Set([
    "street_number",
    "route",
    "plus_code",
    "premise",
    "subpremise",
  ]);
  const predictionIsStreetNoise =
    !predictionMain ||
    streetLikeTypes.has(predictionMain.toLowerCase()) ||
    isPlusCode(predictionMain);

  let name = "";
  if (predictionMain && !predictionIsStreetNoise) {
    name = predictionMain;
  } else if (detailsName && (isEstablishment || !locality || detailsName !== locality)) {
    name = detailsName;
  } else {
    name = displayName;
  }

  // --- Product city (sc) ---
  let city = "";
  if (locality && isUsableAreaLabel(locality) && !isCountryOrStateToken(locality)) {
    city = locality;
  } else {
    city =
      parseCityFromFormattedAddress(fullAddress, state) ||
      (isUsableAreaLabel(displayName) ? displayName : "") ||
      name ||
      fullAddress;
  }

  // --- Product area / locality (slc) — NEVER POI establishment title ---
  const poiTitleKey = normalizeKey(name);
  const detailsNameKey = normalizeKey(detailsName);

  const isPoiTitle = (value: string) => {
    const key = normalizeKey(value);
    if (!key) return false;
    if (isEstablishment && (key === poiTitleKey || key === detailsNameKey)) return true;
    if (key === poiTitleKey && isEstablishment) return true;
    return false;
  };

  let area = "";
  if (isUsableAreaLabel(neighborhood) && !isPoiTitle(neighborhood)) {
    area = neighborhood;
  } else if (isUsableAreaLabel(subLocality) && !isPoiTitle(subLocality)) {
    area = subLocality;
  } else if (
    isUsableAreaLabel(displayName) &&
    !isPoiTitle(displayName) &&
    normalizeKey(displayName) !== normalizeKey(city)
  ) {
    area = displayName;
  } else {
    area =
      extractAreaFromFormattedAddress(fullAddress, city) ||
      city;
  }

  // Hard rule: area must never equal POI establishment title
  if (isEstablishment && isPoiTitle(area)) {
    area =
      (isUsableAreaLabel(neighborhood) && !isPoiTitle(neighborhood) && neighborhood) ||
      (isUsableAreaLabel(subLocality) && !isPoiTitle(subLocality) && subLocality) ||
      extractAreaFromFormattedAddress(fullAddress, city) ||
      city;
  }

  return {
    placeId: result.place_id,
    name,
    address: fullAddress || name,
    lat,
    lng,
    displayName,
    locality,
    subLocality,
    neighborhood,
    state,
    country,
    city: city.trim(),
    area: area.trim() || city.trim(),
  };
}

export function easyModePlaceToSearchParams(place: EasyModePlaceSelection): {
  city: string;
  locality: string;
  fullAddress: string;
  placeId: string;
  lat: string;
  lng: string;
} {
  return {
    city: place.city,
    locality: place.area,
    fullAddress: place.address,
    placeId: place.placeId,
    lat: String(place.lat),
    lng: String(place.lng),
  };
}
